import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { useRail } from "../../../shared/phone/media";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import type { ProductListItem } from "../../../shared/types";
import { ProductLists } from "../../components/lists/ProductLists";
import { NewGoalSheet } from "../../components/NewGoalMenu";
import { Icon } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import { cpr } from "../../lib/format";
import type { ProductGoal, ProfileView } from "../../lib/productsApi";
import { ProductTile } from "./ProductMark";
import { goalLine, goalPill, profileStatus, TRACKING } from "./status";
import { plural } from "./text";
import { useProductPage } from "./useProductPage";
import { useToast } from "./useToast";
import { Can, useCan } from "../../context/AuthContext";

/** Where a product's profile stands, in the words the Products list and the product's row use. */
const PROFILE_LINE: Record<ProductListItem["profile"]["state"], string> = {
  confirmed: "Profile confirmed",
  learning: "Learning the business",
  question: "Waiting for your answer",
  draft: "Waiting for you to confirm",
  none: "No profile yet",
  failed: "Learning stopped",
};

function ProductRow({ p, on, replace }: { p: ProductListItem; on: boolean; replace: boolean }) {
  const state = p.profile.state;
  const waits = state === "question" || state === "draft";
  return (
    <Link className={on ? "pn-row pd-prow on" : "pn-row pd-prow"} to={`/products/${encodeURIComponent(p.id)}`} replace={replace} aria-current={on ? "page" : undefined}>
      <ProductTile id={p.id} name={p.name} logo={!!p.logoR2Key} size={48} />
      <span className="m">
        <span className="t">{p.name}</span>
        {p.description && <span className="s">{p.description}</span>}
        <span className="pd-st">
          {state === "confirmed" ? (
            <span className="pd-ok">
              <Icon name="check" />
              {PROFILE_LINE.confirmed}
            </span>
          ) : (
            <span className={waits ? "pd-wait" : undefined}>{PROFILE_LINE[state]}</span>
          )}
          {(state === "confirmed" || p.goals > 0) && <span>, {p.goals ? plural(p.goals, "goal") : "no goals yet"}</span>}
        </span>
      </span>
      <Chevron />
    </Link>
  );
}

/**
 * The Products tab: one row per product, the whole row opening it. On an iPad it is the list beside the
 * picked product, which it highlights.
 */
export function ProductsList({ items, err, selected }: { items: ProductListItem[] | null; err: string | null; selected?: string }) {
  const rail = useRail();
  return (
    <div className="pn-screen">
      <PhoneNav
        title="Products"
        large
        actions={
          <Can min="editor">
            <Link className="pn-tbtn pill" to="/products/new" aria-label="Add a product">
              <Icon name="plus" />
              <span>New</span>
            </Link>
          </Can>
        }
      />
      <div className="pn-stack">
        {err && <p className="pn-note">Couldn't load products: {err}</p>}
        {!items && !err && <p className="pn-note">Loading…</p>}
        {items && items.length === 0 && (
          <div className="pn-empty">
            <b>No products yet</b>
            Add one, and the agent learns the business.
          </div>
        )}
        {items && items.length > 0 && (
          <div className="pn-grp">
            {items.map((p) => (
              // Why: on an iPad picking a product changes the pane beside the list, not a step to go back through.
              <ProductRow key={p.id} p={p} on={p.id === selected} replace={rail && !!selected} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const GOAL_TONE: Record<string, string> = {
  live: "good",
  planning: "acc",
  plan_ready: "violet",
  approved: "warn",
  getting_ready: "warn",
  paused: "warn",
};

function GoalRow({ g }: { g: ProductGoal }) {
  const { title, sub } = goalLine(g);
  const tone = GOAL_TONE[g.state];
  return (
    <Link className="pn-row wrap pd-grow" to={`/goals/${encodeURIComponent(g.id)}`}>
      <span className="m">
        <span className="pd-gtop">
          <span className={tone ? `pn-pill ${tone}` : "pn-pill"}>
            <i />
            {goalPill(g.state)[1]}
          </span>
        </span>
        <span className="t">{title}</span>
        {sub && <span className="s">{sub}</span>}
      </span>
      <Chevron />
    </Link>
  );
}

function OpensRow({ g }: { g: ProductGoal }) {
  const o = g.opens!;
  const ok = o.tracking !== "not";
  // Why: the row opens the goal's ads to change where they open, an Editor's; a Viewer reads it here.
  const row = (
    <>
      <span className="m">
        <span className="t">{goalLine(g).title}</span>
        <span className="s">{o.opens === "metaform" ? "Meta's form" : (o.page ?? "No page yet")}</span>
        <span className="pd-pl">
          <span className={ok ? "pn-pill good" : "pn-pill warn"}>
            <i />
            {TRACKING[o.tracking]}
          </span>
        </span>
      </span>
    </>
  );
  return useCan()("editor") ? (
    <Link className="pn-row wrap pd-grow" to={`/goals/${encodeURIComponent(g.id)}/ads`}>
      {row}
      <Chevron />
    </Link>
  ) : (
    <div className="pn-row wrap pd-grow static">{row}</div>
  );
}

/** The row into the business profile, saying where it stands. */
function profileRow(view: ProfileView): { sub: string; tone?: "warn" } {
  const { step } = profileStatus(view);
  const c = view.confirmed;
  const now = step === "learning" ? PROFILE_LINE.learning : step === "question" ? PROFILE_LINE.question : step === "draft" ? PROFILE_LINE.draft : null;
  if (c) return now ? { sub: `Version ${c.version} · ${now}`, tone: step === "learning" ? undefined : "warn" } : { sub: `Confirmed by you · version ${c.version}` };
  if (now) return { sub: now, tone: step === "learning" ? undefined : "warn" };
  const stopped = view.learnRun && view.learnRun.state !== "done";
  return { sub: stopped ? PROFILE_LINE.failed : "No profile yet. Every goal starts from one.", tone: "warn" };
}

/** What the Assets row says: how many folders and how many partners can see. */
function useAssetsLine(id: string): string | undefined {
  const [line, setLine] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setLine(undefined);
    api.assets
      .list(id)
      .then((d) => {
        if (!alive) return;
        const shared = d.folders.filter((f) => f.sharedWithPartners).length;
        const loose = d.assets.filter((a) => a.folderId === null).length;
        if (d.folders.length) setLine(`${plural(d.folders.length, "folder")} · ${shared} shared with partners`);
        else setLine(loose ? plural(loose, "file") : "Nothing yet");
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id]);
  return line;
}

/**
 * A product on a phone or an iPad: its name large under its mark (tap the mark to change the logo), then its
 * goals, where their ads open, its client lists, and rows into its profile, Partners and Assets. `split`: it
 * sits beside the Products list on an iPad, with nowhere to go back to.
 */
export function ProductScreen({ id, split = false }: { id: string; split?: boolean }) {
  const { toast, toastNode } = useToast();
  const { data, err, setLogo } = useProductPage(id, toast);
  const assets = useAssetsLine(id);
  const [newGoal, setNewGoal] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const can = useCan();
  const back = split ? undefined : { to: "/products", label: "Products" };

  // Why: on an iPad the pane keeps its scroll from the product before.
  useEffect(() => {
    if (split) document.querySelector(".pn-pane-detail")?.scrollTo({ top: 0 });
  }, [id, split]);

  if (err || !data)
    return (
      <div className="pn-screen">
        <PhoneNav title="" back={back} />
        {err ? (
          <div className="pn-empty">
            <b>{err.status === 404 ? "This product is not here" : "Couldn't load this product"}</b>
            {err.status === 404 ? "It does not exist, or it belongs to another account." : err.message}
          </div>
        ) : (
          <p className="pn-note">Loading…</p>
        )}
      </div>
    );

  const { product, partnersLane: lane, coldEmail } = data.detail;
  const base = `/products/${encodeURIComponent(product.id)}`;
  const opens = data.goals.filter((g) => g.opens && g.state !== "ended");
  const prof = profileRow(data.profile);
  const partners =
    lane.partners > 0 || lane.sales > 0
      ? `${lane.partners} active · ${plural(lane.sales, "sale")}${lane.sales > 0 ? ` · ${cpr(lane.costPerSaleCents)} per sale` : ""}`
      : "No partners yet";

  return (
    <div className="pn-screen pd-prod">
      <PhoneNav
        title={product.name}
        large
        back={back}
        actions={
          <Can min="editor">
            <button type="button" className="pn-tbtn pill" onClick={() => setNewGoal(true)}>
              <Icon name="plus" />
              <span>New goal</span>
            </button>
          </Can>
        }
      />
      <div className="pn-stack">
        <header className="pd-head">
          <button type="button" className="pd-logo" onClick={() => fileRef.current?.click()} aria-label="Change the logo" disabled={!can("editor")}>
            <ProductTile id={product.id} name={product.name} logo={!!product.logoR2Key} size={64} />
            {can("editor") && (
              <span className="pd-cam">
                <Icon name="pen" />
              </span>
            )}
          </button>
          <div className="pd-ht">
            <h1>{product.name}</h1>
            {product.description && <p>{product.description}</p>}
          </div>
        </header>
        <section>
          <div className="pn-sh">
            <h2>Goals</h2>
          </div>
          <div className="pn-grp">
            {data.goals.length ? (
              data.goals.map((g) => <GoalRow key={g.id} g={g} />)
            ) : (
              <div className="pn-row static">
                <span className="m">
                  <span className="s">No goals yet. Tap New goal to set one.</span>
                </span>
              </div>
            )}
          </div>
        </section>
        {opens.length > 0 && (
          <section>
            <div className="pn-sh">
              <h2>Where each goal's ads open</h2>
            </div>
            <div className="pn-grp">
              {opens.map((g) => (
                <OpensRow key={g.id} g={g} />
              ))}
            </div>
          </section>
        )}
        <ProductLists productId={product.id} productName={product.name} />
        <div className="pn-grp">
          {can("editor") && <PnRow to={`${base}/profile`} icon={<Icon name="doc" />} iconTone={prof.tone} title="Business profile" sub={prof.sub} />}
          <PnRow to={can("admin") ? `${base}/partners` : undefined} icon={<Icon name="pdHandshake" />} iconTone="violet" title="Partners" sub={partners} />
          <PnRow to={`${base}?assets`} icon={<Icon name="photo" />} iconTone="plain" title="Assets" sub={assets} />
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          void setLogo(file);
        }}
      />
      <NewGoalSheet open={newGoal} onClose={() => setNewGoal(false)} to={`${base}/goals/new`} coldEmail={coldEmail} />
      {toastNode}
    </div>
  );
}
